const DB_NAME='bookmarks-manager-pro-local';
const DB_VERSION=1;
const CACHE_SCHEMA_VERSION=1;
export const QUEUE_SCHEMA_VERSION=1;
const SNAPSHOT_STORE='snapshots';
const MUTATION_STORE='mutations';
const META_STORE='meta';

let dbPromise=null;

const canUseIndexedDB=()=> typeof window!=='undefined' && 'indexedDB' in window;
const notifyQueueChanged=(userId)=> {
  if (typeof window!=='undefined') window.dispatchEvent(new CustomEvent('bmp:queue-changed',{detail:{userId}}));
};

const openDatabase=()=> {
  if (!canUseIndexedDB()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise=new Promise((resolve,reject)=> {
    const request=window.indexedDB.open(DB_NAME,DB_VERSION);
    request.onupgradeneeded=()=> {
      const db=request.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) db.createObjectStore(SNAPSHOT_STORE,{keyPath:'userId'});
      if (!db.objectStoreNames.contains(MUTATION_STORE)) {
        const mutations=db.createObjectStore(MUTATION_STORE,{keyPath:'id',autoIncrement:true});
        mutations.createIndex('userId','userId',{unique:false});
        mutations.createIndex('entityTarget',['userId','entity','targetId'],{unique:false});
      }
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE,{keyPath:'key'});
    };
    request.onsuccess=()=> {
      const db=request.result;
      db.onversionchange=()=> {db.close();dbPromise=null;};
      resolve(db);
    };
    request.onerror=()=> reject(request.error || new Error('Unable to open local database'));
    request.onblocked=()=> console.warn('IndexedDB upgrade is blocked by another open app tab.');
  });
  return dbPromise;
};

const requestToPromise=(request)=> new Promise((resolve,reject)=> {
  request.onsuccess=()=> resolve(request.result);
  request.onerror=()=> reject(request.error || new Error('IndexedDB request failed'));
});

const transactionDone=(transaction)=> new Promise((resolve,reject)=> {
  transaction.oncomplete=()=> resolve();
  transaction.onerror=()=> reject(transaction.error || new Error('IndexedDB transaction failed'));
  transaction.onabort=()=> reject(transaction.error || new Error('IndexedDB transaction aborted'));
});

const withStore=async (storeName,mode,callback)=> {
  const db=await openDatabase();
  if (!db) return null;
  const transaction=db.transaction(storeName,mode);
  const store=transaction.objectStore(storeName);
  const result=await callback(store,transaction);
  await transactionDone(transaction);
  return result;
};

const cloneArray=(value)=> Array.isArray(value) ? value : [];

export const getCachedLibrary=async (userId)=> {
  if (!userId) return null;
  try {
    const snapshot=await withStore(SNAPSHOT_STORE,'readonly',store=> requestToPromise(store.get(userId)));
    if (!snapshot || snapshot.schemaVersion!==CACHE_SCHEMA_VERSION) return null;
    return {bookmarks:cloneArray(snapshot.bookmarks),folders:cloneArray(snapshot.folders),settings:snapshot.settings || null,updatedAt:snapshot.updatedAt || 0};
  } catch (error) {
    console.warn('Unable to read local bookmark cache:',error);
    return null;
  }
};

export const saveCachedLibrary=async (userId,patch={})=> {
  if (!userId) return false;
  try {
    await withStore(SNAPSHOT_STORE,'readwrite',async store=> {
      const existing=await requestToPromise(store.get(userId)) || {userId,schemaVersion:CACHE_SCHEMA_VERSION,bookmarks:[],folders:[],settings:null,updatedAt:0};
      await requestToPromise(store.put({...existing,...patch,userId,schemaVersion:CACHE_SCHEMA_VERSION,updatedAt:Date.now()}));
    });
    return true;
  } catch (error) {
    console.warn('Unable to save local bookmark cache:',error);
    return false;
  }
};

export const clearCachedLibrary=async (userId)=> {
  if (!userId) return false;
  try {await withStore(SNAPSHOT_STORE,'readwrite',store=> requestToPromise(store.delete(userId)));return true;}
  catch (error) {console.warn('Unable to clear local bookmark cache:',error);return false;}
};

export const queueMutation=async (userId,mutation)=> {
  if (!userId) throw new Error('Cannot queue a change without a user');
  const record={queueVersion:mutation.queueVersion || QUEUE_SCHEMA_VERSION,userId,entity:mutation.entity,operation:mutation.operation,targetId:mutation.targetId || null,payload:mutation.payload || {},createdAt:mutation.createdAt || Date.now(),attempts:mutation.attempts || 0};
  const id=await withStore(MUTATION_STORE,'readwrite',store=> requestToPromise(store.add(record)));
  notifyQueueChanged(userId);
  return {...record,id};
};

export const getQueuedMutations=async (userId)=> {
  if (!userId) return [];
  try {
    const db=await openDatabase();
    if (!db) return [];
    const transaction=db.transaction(MUTATION_STORE,'readonly');
    const records=await requestToPromise(transaction.objectStore(MUTATION_STORE).index('userId').getAll(userId));
    await transactionDone(transaction);
    return (records || []).map(record=> ({queueVersion:record.queueVersion || 1,...record})).sort((a,b)=> (a.id || 0)-(b.id || 0));
  } catch (error) {
    console.warn('Unable to read pending changes:',error);
    return [];
  }
};

export const getPendingMutationCount=async (userId)=> {
  if (!userId) return 0;
  try {
    const db=await openDatabase();
    if (!db) return 0;
    const transaction=db.transaction(MUTATION_STORE,'readonly');
    const count=await requestToPromise(transaction.objectStore(MUTATION_STORE).index('userId').count(userId));
    await transactionDone(transaction);
    return count || 0;
  } catch {return 0;}
};

export const removeQueuedMutation=async (id,userId=null)=> {
  if (id===null || id===undefined) return false;
  await withStore(MUTATION_STORE,'readwrite',store=> requestToPromise(store.delete(id)));
  if (userId) notifyQueueChanged(userId);
  return true;
};

export const updateQueuedMutation=async (id,patch)=> {
  if (id===null || id===undefined) return null;
  return withStore(MUTATION_STORE,'readwrite',async store=> {
    const existing=await requestToPromise(store.get(id));
    if (!existing) return null;
    const next={...existing,...patch};
    await requestToPromise(store.put(next));
    return next;
  });
};

export const cancelQueuedEntity=async (userId,entity,targetId)=> {
  if (!userId || !entity || !targetId) return 0;
  const db=await openDatabase();
  if (!db) return 0;
  const transaction=db.transaction(MUTATION_STORE,'readwrite');
  const store=transaction.objectStore(MUTATION_STORE);
  const records=await requestToPromise(store.index('userId').getAll(userId));
  let removed=0;
  for (const record of records || []) {
    if (record.entity===entity && record.targetId===targetId) {store.delete(record.id);removed+=1;}
  }
  await transactionDone(transaction);
  if (removed) notifyQueueChanged(userId);
  return removed;
};

export const remapQueuedEntityId=async (userId,entity,oldId,newId)=> {
  if (!userId || !oldId || !newId || oldId===newId) return;
  const db=await openDatabase();
  if (!db) return;
  const transaction=db.transaction(MUTATION_STORE,'readwrite');
  const store=transaction.objectStore(MUTATION_STORE);
  const records=await requestToPromise(store.index('userId').getAll(userId));
  for (const record of records || []) {
    let changed=false;
    const next={...record,payload:{...(record.payload || {})}};
    if (record.entity===entity && record.targetId===oldId) {next.targetId=newId;changed=true;}
    if (entity==='folder') {
      if (next.payload.folderId===oldId) {next.payload.folderId=newId;changed=true;}
      if (next.payload.parent===oldId) {next.payload.parent=newId;changed=true;}
    }
    if (changed) store.put(next);
  }
  await transactionDone(transaction);
};

export const setLocalMeta=async (key,value)=> {
  if (!key) return false;
  try {await withStore(META_STORE,'readwrite',store=> requestToPromise(store.put({key,value,updatedAt:Date.now()})));return true;}
  catch (error) {console.warn('Unable to save local metadata:',error);return false;}
};

export const getLocalMeta=async (key)=> {
  if (!key) return null;
  try {const record=await withStore(META_STORE,'readonly',store=> requestToPromise(store.get(key)));return record?.value ?? null;}
  catch {return null;}
};

export const seedData=async ()=> undefined;
export const bookmarkDB={bookmarks:{toArray:()=> Promise.resolve([]),add:()=> Promise.resolve(),update:()=> Promise.resolve(),delete:()=> Promise.resolve(),bulkAdd:()=> Promise.resolve(),bulkDelete:()=> Promise.resolve()},folders:{toArray:()=> Promise.resolve([]),add:()=> Promise.resolve(),update:()=> Promise.resolve(),delete:()=> Promise.resolve(),bulkAdd:()=> Promise.resolve(),bulkDelete:()=> Promise.resolve()}};